import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import AdminHeader from '../components/AdminHeader';
import { ArrowLeft, Search, Users, Mail, Calendar, DollarSign, Key } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!auth.isAuthenticated() || !auth.getUser()?.is_admin) {
      navigate('/login');
      return;
    }

    loadUsers();
  }, [navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const token = auth.getToken();
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/users?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AdminHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-6 bg-gray-800" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-32 bg-gray-800" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Admin Header */}
      <AdminHeader />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-900/50 border-red-500/50 text-red-200">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Search */}
        <Card className="mb-6 bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-white">Search Users</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="flex gap-4">
              <Input
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-gray-800/50 border-purple-500/30 text-white placeholder:text-gray-400"
              />
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* User List */}
        <div className="space-y-4">
          {users.length === 0 ? (
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardContent className="py-12">
                <div className="text-center">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-white">No users found</h3>
                  <p className="mt-1 text-sm text-gray-400">
                    Try adjusting your search.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            users.map((user) => (
              <Card key={user.id} className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-lg font-semibold text-white">{user.name}</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-300">
                          <Mail className="h-4 w-4 text-purple-400" />
                          <span>{user.email}</span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-300">
                          <Calendar className="h-4 w-4 text-purple-400" />
                          <span>Joined {formatDate(user.created_at)}</span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-300">
                          <Key className="h-4 w-4 text-purple-400" />
                          <span>{user.license_count} license(s)</span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-300">
                          <DollarSign className="h-4 w-4 text-purple-400" />
                          <span>${user.total_spent.toFixed(2)} spent</span>
                        </div>
                      </div>

                      <div className="mt-3 flex gap-2">
                        <Badge variant="secondary" className="bg-gray-700 text-gray-200">
                          {user.total_purchases} purchase{user.total_purchases !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

